import { useState, useEffect } from "react";
import { toast } from "sonner";
import { funeralStoreService } from "../../services/funeralStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Package, Search, Upload, X, Info, DollarSign, Image as ImageIcon, Settings, Layers, Star, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { MultiSelect } from "@/components/ui/multi-select";
import { Badge } from "@/components/ui/badge";
import { getImageUrl } from "../../utils/imageUtils";
import { productsService } from "../../services/products";
import { customizationsService } from "../../services/customizations";

export default function AdminFuneralStoreProducts() {
  const [products, setProducts] = useState([]);
  const [allCustomizations, setAllCustomizations] = useState([]);
  const [availableCollections, setAvailableCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [appliedKeyword, setAppliedKeyword] = useState("");
  const [filterCollection, setFilterCollection] = useState([]);
  const [activeTab, setActiveTab] = useState("general");
  const [editingProductVariants, setEditingProductVariants] = useState([]);
  const [parentProductName, setParentProductName] = useState(null);
  const [parentProductBeforeVariant, setParentProductBeforeVariant] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    base_price: "",
    overridden_price: "",
    sku: "",
    inventory_quantity: 0,
    images: [],
    image_url: "",
    is_active: true,
    is_available: true,
    is_addon: false,
    addons: [],
    collections: [],
    customizations: { mandatory: [], optional: [] },
    is_variant: false,
    parent_id: null,
    variant_name: ""
  });

  useEffect(() => {
    fetchData();
  }, [page, limit, appliedKeyword, filterCollection]); // Refetch on changes

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = { 
        page, 
        limit,
        keyword: appliedKeyword, 
        only_parents: true,
        collection_id: filterCollection.length > 0 ? filterCollection.join(",") : undefined
      };

      const [productsResponse, collectionsData, customizationsData] = await Promise.all([
        funeralStoreService.admin.products.getAll(params),
        funeralStoreService.admin.collections.getAll({ limit: 1000 }), // Fetch more collections for the dropdown
        customizationsService.getAll()
      ]);

      const productsData = productsResponse.data || [];
      setTotalItems(productsResponse.totalItems || 0);
      setTotalPages(productsResponse.totalPages || 1);

      setProducts(productsData);
      setAvailableCollections(Array.isArray(collectionsData.data || collectionsData) ? (collectionsData.data || collectionsData).map(c => ({ 
        label: c.name, 
        value: c.collection_id,
        is_active: c.is_active 
      })) : []);
      setAllCustomizations((customizationsData || [])
        .filter(c => c.is_active)
        .map(c => ({
          label: `${c.name} ($${c.price})`,
          value: c.customization_id
        }))
      );
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const fetchEditingProductVariants = async (parentId) => {
    try {
      const response = await funeralStoreService.admin.products.getAll({ parent_id: parentId, limit: 1000 });
      setEditingProductVariants(response.data || []);
    } catch (error) {
      toast.error("Failed to fetch variants");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim()) return toast.error("Name is required");
    if (!formData.base_price) return toast.error("Base price is required");
    if (formData.overridden_price && parseFloat(formData.overridden_price) < parseFloat(formData.base_price)) {
      return toast.error("Override Price cannot be less than Base Price.");
    }

    try {
      const data = {
        ...formData,
        base_price: parseFloat(formData.base_price),
        overridden_price: formData.overridden_price ? parseFloat(formData.overridden_price) : 0,
        inventory_quantity: parseInt(formData.inventory_quantity)
      };

      if (editingProduct) {
        await funeralStoreService.admin.products.update(editingProduct.product_id, data);
        toast.success("Product updated");
      } else {
        await funeralStoreService.admin.products.create(data);
        toast.success(formData.is_variant ? "Variant created" : "Product created");
      }

      if (formData.is_variant && formData.parent_id) {
        if (parentProductBeforeVariant) {
            const parent = parentProductBeforeVariant;
            setParentProductBeforeVariant(null);
            handleEdit(parent);
            setTimeout(() => setActiveTab("variants"), 0);
        } else {
            fetchEditingProductVariants(formData.parent_id);
            resetForm();
            setActiveTab("variants");
        }
      } else {
        setDialogOpen(false);
        resetForm();
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to save product");
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setParentProductName(null);
    if (!product.is_variant && !product.is_addon) {
      fetchEditingProductVariants(product.product_id);
    }
    setFormData({
      name: product.name,
      description: product.description || "",
      base_price: product.base_price.toString(),
      overridden_price: product.overridden_price ? product.overridden_price.toString() : "",
      base_price: product.base_price.toString(),
      overridden_price: product.overridden_price ? product.overridden_price.toString() : "",
      sku: product.sku || "",
      inventory_quantity: product.inventory_quantity || 0,
      images: product.images || [],
      image_url: product.image_url || "",
      is_active: product.is_active !== undefined ? product.is_active : true,
      is_available: product.is_available !== undefined ? product.is_available : true,
      is_addon: product.is_addon || false,
      addons: product.addons || [],
      collections: product.collections || [],
      customizations: product.customizations || { mandatory: [], optional: [] },
      is_variant: product.is_variant || false,
      parent_id: product.parent_id || null,
      variant_name: product.variant_name || ""
    });
    setDialogOpen(true);
    setActiveTab("general");
  };

  const handleDelete = async (productId, isVariant = false, parentId = null) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await funeralStoreService.admin.products.delete(productId);
      toast.success("Product deleted");
      if (isVariant && parentId) {
        fetchEditingProductVariants(parentId);
      } else {
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: "",
      description: "",
      base_price: "",
      overridden_price: "",
      base_price: "",
      overridden_price: "",
      sku: "",
      inventory_quantity: 0,
      images: [],
      image_url: "",
      is_active: true,
      is_available: true,
      is_addon: false,
      addons: [],
      collections: [],
      customizations: { mandatory: [], optional: [] },
      is_variant: false,
      parent_id: null,
      variant_name: ""
    });
    setParentProductName(null);
    setParentProductBeforeVariant(null);
    setEditingProductVariants([]);
    setActiveTab("general");
  };

  const availableAddons = products
    .filter(p => p.is_addon && p.product_id !== editingProduct?.product_id)
    .map(p => ({
      label: `${p.name} ($${p.base_price})`,
      value: p.product_id
    }));

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Funeral Supplies</h2>
          <p className="text-slate-500">Manage exclusive products for Funeral Homes</p>
        </div>
        <div className="flex items-center gap-3">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search products..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setAppliedKeyword(searchKeyword);
                    setPage(1);
                  }
                }}
                className="pl-10 h-10"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-10 px-3"
              onClick={() => { setAppliedKeyword(searchKeyword); setPage(1); }}
            >
              <Search className="w-4 h-4" />
            </Button>
            {appliedKeyword && (
              <Button
                variant="ghost"
                size="sm"
                className="h-10 px-2 text-slate-500"
                onClick={() => { setSearchKeyword(""); setAppliedKeyword(""); setPage(1); }}
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
            <div className="w-56">
              <MultiSelect
                options={availableCollections}
                selected={filterCollection}
                onChange={(val) => { setFilterCollection(val); setPage(1); }}
                placeholder="Filter by collection"
              />
            </div>
          <Sheet open={dialogOpen} onOpenChange={setDialogOpen}>
            <SheetTrigger asChild>
              <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
              <SheetHeader>
                <div className="flex items-center gap-2">
                   {formData.is_variant && parentProductBeforeVariant && (
                    <Button variant="outline" size="sm" onClick={() => {
                        const parent = parentProductBeforeVariant;
                        setParentProductBeforeVariant(null);
                        handleEdit(parent);
                        setTimeout(() => setActiveTab("variants"), 0);
                    }}>
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Back to Parent
                    </Button>
                   )}
                   <SheetTitle>{editingProduct ? (formData.is_variant ? "Edit Variant" : "Edit Product") : (formData.is_variant ? `Add Variant for ${parentProductName}` : "New Product")}</SheetTitle>
                </div>
              </SheetHeader>

              <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className={cn("grid w-full", (editingProduct && !editingProduct.is_variant && !editingProduct.is_addon) ? "grid-cols-5" : "grid-cols-4")}>
                    <TabsTrigger value="general">General</TabsTrigger>
                    <TabsTrigger value="pricing">Pricing</TabsTrigger>
                    <TabsTrigger value="images">Images</TabsTrigger>
                    <TabsTrigger value="custom">Custom</TabsTrigger>
                    {(editingProduct && !editingProduct.is_variant && !editingProduct.is_addon) && (
                        <TabsTrigger value="variants">Variants</TabsTrigger>
                    )}
                  </TabsList>

                  <TabsContent value="general" className="space-y-4 mt-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {formData.is_variant && (
                            <div className="space-y-2">
                                <Label>Option Name *</Label>
                                <Input required value={formData.variant_name} onChange={e => setFormData({...formData, variant_name: e.target.value})} placeholder="e.g. Red, XL" />
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label>{formData.is_variant ? "Parent Name" : "Name"} *</Label>
                            <Input 
                                required 
                                value={formData.name} 
                                onChange={e => setFormData({...formData, name: e.target.value})} 
                                disabled={formData.is_variant} 
                                className={cn("h-12", formData.is_variant && "bg-slate-50 text-slate-500 cursor-not-allowed")} 
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>SKU</Label>
                            <Input value={formData.sku} onChange={e => setFormData({...formData, sku: e.target.value})} />
                        </div>
                    </div>
                    {!formData.is_variant && (
                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows={3} />
                        </div>
                    )}
                    {!formData.is_variant && (
                        <div className="space-y-2">
                            <Label>Collections</Label>
                            <MultiSelect 
                                options={availableCollections}
                                selected={formData.collections}
                                onChange={s => setFormData({...formData, collections: s})}
                                placeholder="Select collections..."
                            />
                        </div>
                    )}
                    <div className="space-y-4 pt-4 border-t">
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                                <Checkbox id="is_active" checked={formData.is_active} onCheckedChange={c => setFormData({...formData, is_active: c})} />
                                <Label htmlFor="is_active" className="cursor-pointer">Visible</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Checkbox id="is_available" checked={formData.is_available} onCheckedChange={c => setFormData({...formData, is_available: c})} />
                                <Label htmlFor="is_available" className="cursor-pointer">Purchasable</Label>
                            </div>
                        </div>
                        {!formData.is_variant && (
                            <div className="flex items-center space-x-2">
                                <Checkbox id="is_addon" checked={formData.is_addon} onCheckedChange={c => setFormData({...formData, is_addon: c})} />
                                <Label htmlFor="is_addon" className="cursor-pointer">Mark as Add-on</Label>
                            </div>
                        )}
                        {!formData.is_addon && !formData.is_variant && (
                             <div className="space-y-2">
                                <Label>Add-ons</Label>
                                <MultiSelect 
                                    options={availableAddons}
                                    selected={formData.addons}
                                    onChange={s => setFormData({...formData, addons: s})}
                                    placeholder="Choose addon products..."
                                />
                             </div>
                        )}
                    </div>
                  </TabsContent>

                  <TabsContent value="pricing" className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Base Price ($) *</Label>
                            <Input type="number" step="0.01" value={formData.base_price} onChange={e => setFormData({...formData, base_price: e.target.value})} required />
                        </div>
                        <div className="space-y-2">
                            <Label>Inventory</Label>
                            <Input type="number" value={formData.inventory_quantity} onChange={e => setFormData({...formData, inventory_quantity: e.target.value})} />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>Overridden Price ($) <Badge variant="outline" className="ml-2 text-xs">Admin Only</Badge></Label>
                        <Input type="number" step="0.01" value={formData.overridden_price} onChange={e => setFormData({...formData, overridden_price: e.target.value})} placeholder="Fixed price across all stores" />
                    </div>
                  </TabsContent>

                  <TabsContent value="images" className="space-y-4 mt-4">
                    <div className="border-2 border-dashed rounded-xl p-4 text-center cursor-pointer relative hover:bg-slate-50">
                        <input 
                            type="file" multiple accept="image/*" className="absolute inset-0 opacity-0 cursor-pointer"
                            onChange={async (e) => {
                                const files = Array.from(e.target.files);
                                if (files.length === 0) return;
                                const fd = new FormData();
                                files.forEach(f => fd.append('images', f));
                                try {
                                    const res = await productsService.uploadImages(fd);
                                    const newImgs = res.urls.map(url => ({ url }));
                                    setFormData(prev => ({
                                        ...prev,
                                        images: [...prev.images, ...newImgs],
                                        image_url: prev.image_url || res.urls[0]
                                    }));
                                    toast.success("Images uploaded");
                                } catch (err) { toast.error("Upload failed"); }
                            }}
                        />
                        <Upload className="w-8 h-8 mx-auto text-slate-400" />
                        <p className="text-sm text-slate-500">Click to upload images</p>
                    </div>
                    <div className="grid grid-cols-4 gap-2 mt-2">
                        {formData.images.map((img, i) => (
                            <div key={i} className={`relative aspect-square rounded-lg border-2 ${formData.image_url === img.url ? 'border-indigo-600 ring-2 ring-indigo-50' : 'border-slate-200'}`}>
                                <img src={getImageUrl(img.url)} className="w-full h-full object-cover rounded-md" />
                                <button type="button" onClick={() => setFormData({ ...formData, image_url: img.url })} className="absolute top-1 left-1 p-0.5 bg-white rounded-full shadow-sm">
                                    <Star className={`w-3 h-3 ${formData.image_url === img.url ? 'fill-indigo-600 text-indigo-600' : 'text-slate-400'}`} />
                                </button>
                                <button type="button" onClick={() => setFormData({ ...formData, images: formData.images.filter((_, idx) => idx !== i) })} className="absolute top-1 right-1 p-0.5 bg-white rounded-full shadow-sm text-red-500">
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="custom" className="space-y-4 mt-4">
                    <div className="space-y-2">
                        <Label>Mandatory Customization</Label>
                        <Select
                          value={formData.customizations?.mandatory?.[0] || "none"}
                          onValueChange={(val) => setFormData(prev => ({
                            ...prev,
                            customizations: {
                              ...prev.customizations,
                              mandatory: val !== "none" ? [val] : []
                            }
                          }))}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select a mandatory customization..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {allCustomizations.map((c) => (
                              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500">A mandatory customization that customers MUST configure.</p>
                    </div>
                    <div className="space-y-2">
                        <Label>Optional Customization</Label>
                        <Select
                          value={formData.customizations?.optional?.[0] || "none"}
                          onValueChange={(val) => setFormData(prev => ({
                            ...prev,
                            customizations: {
                              ...prev.customizations,
                              optional: val !== "none" ? [val] : []
                            }
                          }))}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select an optional customization..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {allCustomizations.map((c) => (
                              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500">An optional customization that customers CAN select if desired.</p>
                    </div>
                  </TabsContent>

                  <TabsContent value="variants" className="space-y-4 mt-4">
                      <div className="flex items-center justify-between">
                          <Label className="font-semibold">Variants List</Label>
                          <Button size="sm" variant="outline" type="button" onClick={() => {
                              setParentProductBeforeVariant(editingProduct);
                              setParentProductName(editingProduct.name);
                              setFormData({
                                ...formData,
                                is_variant: true,
                                parent_id: editingProduct.product_id,
                                variant_name: "",
                                sku: editingProduct.sku ? `${editingProduct.sku}-VAR` : "",
                                inventory_quantity: 0
                              });
                              setEditingProduct(null);
                              setActiveTab("general");
                          }}>
                              <Plus className="w-4 h-4 mr-1" /> Create Variant
                          </Button>
                      </div>
                      <div className="border rounded-lg overflow-hidden">
                          <Table>
                              <TableBody>
                                  {editingProductVariants.map(v => (
                                      <TableRow key={v.product_id}>
                                          <TableCell className="py-2">{v.variant_name}</TableCell>
                                          <TableCell className="py-2">${v.base_price.toFixed(2)}</TableCell>
                                          <TableCell className="py-2 text-right">
                                              <Button variant="ghost" size="sm" onClick={() => {
                                                   setParentProductBeforeVariant(editingProduct);
                                                   handleEdit(v);
                                               }}><Pencil className="w-4 h-4" /></Button>
                                              <Button variant="ghost" size="sm" onClick={() => handleDelete(v.product_id, true, v.parent_id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                                          </TableCell>
                                      </TableRow>
                                  ))}
                              </TableBody>
                          </Table>
                      </div>
                  </TabsContent>
                </Tabs>

                <Button type="submit" className="w-full bg-indigo-600 mt-6">
                  {editingProduct ? "Update Product" : "Create Product"}
                </Button>
              </form>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <div className="card-elevated overflow-hidden relative">
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm font-medium text-slate-600">Loading products...</p>
            </div>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Image</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Stock</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Collections</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.length === 0 && !loading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-12">
                <div className="flex flex-col items-center gap-2 text-slate-400">
                  <Package className="w-12 h-12 mb-2 opacity-20" />
                  <p>No funeral supplies products found</p>
                </div>
              </TableCell></TableRow>
            ) : (
              products.map(p => (
                <TableRow key={p.product_id} className={cn("transition-opacity", !p.is_active && "opacity-60 grayscale-[0.3]")}>
                  <TableCell>
                    {p.image_url ? (
                        <img src={getImageUrl(p.image_url)} className="w-10 h-10 object-cover rounded" />
                    ) : (
                        <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center text-slate-400">
                            <ImageIcon className="w-5 h-5" />
                        </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="text-slate-500">{p.sku || '-'}</TableCell>
                  <TableCell>${p.base_price.toFixed(2)}</TableCell>
                  <TableCell>{p.inventory_quantity}</TableCell>
                  <TableCell>
                      {p.is_addon ? <Badge variant="secondary">Add-on</Badge> : <Badge variant="outline">Main</Badge>}
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "text-[10px] px-2 py-0.5 rounded-full",
                        p.is_active ? "bg-green-50 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"
                      )}
                    >
                      {p.is_active ? 'Visible' : 'Hidden'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {p.collections && p.collections.length > 0 ? (
                        p.collections.map((colId, i) => {
                          const col = availableCollections.find(c => c.value === colId);
                          if (!col) {
                            return <Badge key={i} variant="secondary" className="text-[10px] bg-slate-50 text-slate-400 border-slate-200">Unknown</Badge>;
                          }
                          return (
                            <Badge 
                              key={i} 
                              variant={col.is_active ? "secondary" : "outline"} 
                              className={cn(
                                "text-[10px]",
                                !col.is_active && "text-amber-600 border-amber-200 bg-amber-50"
                              )}
                            >
                              {col.label} {!col.is_active && "(Hidden)"}
                            </Badge>
                          );
                        })
                      ) : (
                        <span className="text-slate-400 text-xs italic">Unassigned</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(p)}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(p.product_id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-4 border-t gap-4 bg-white rounded-b-xl border-x border-b">
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <div>
              Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to <span className="font-medium">{Math.min(page * limit, totalItems)}</span> of <span className="font-medium">{totalItems}</span> results
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-600">Rows per page:</span>
              <Select
                value={limit.toString()}
                onValueChange={(val) => {
                  setLimit(Number(val));
                  setPage(1);
                }}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue placeholder={limit.toString()} />
                </SelectTrigger>
                <SelectContent side="top">
                  {[10, 15, 25, 50, 100].map((pageSize) => (
                    <SelectItem key={pageSize} value={pageSize.toString()}>
                      {pageSize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>

            <div className="flex items-center mx-2 space-x-1">
              {Array.from({ length: totalPages }).map((_, i) => {
                const pageNum = i + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= page - 1 && pageNum <= page + 1)
                ) {
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? "default" : "outline"}
                      size="sm"
                      className={cn(
                        "h-8 w-8 p-0",
                        page === pageNum ? "bg-primary hover:bg-primary/90 text-white" : "text-slate-600 hover:bg-slate-100"
                      )}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                }
                if (pageNum === page - 2 || pageNum === page + 2) {
                  return <span key={pageNum} className="text-slate-400 px-1">...</span>;
                }
                return null;
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
